import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { track, identify, resetAnalytics } from "@/lib/analytics";
import { setSentryUser } from "@/lib/sentry";
import { useQueryClient } from "@tanstack/react-query";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<{ session: Session | null; loading: boolean }>({
    session: null,
    loading: true,
  });
  const { session, loading } = authState;
  const initialSessionResolved = useRef(false);
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      const nextId = s?.user?.id ?? null;
      // Token refresh occasionally fires with a transiently null session
      // (network blip, racing tab). Ignore those — only SIGNED_OUT or
      // USER_DELETED are definitive logouts. This prevents mid-session
      // auth collapse → redirect → white screen.
      const isTransientNull =
        !s &&
        lastUserIdRef.current !== null &&
        _event !== "SIGNED_OUT";
      if (isTransientNull) {
        return;
      }
      // Only clear cache on real user change (sign-in / sign-out / switch).
      if (lastUserIdRef.current !== null && lastUserIdRef.current !== nextId) {
        queryClient.clear();
      }
      lastUserIdRef.current = nextId;
      setAuthState((prev) =>
        initialSessionResolved.current
          ? { session: s, loading: false }
          : { session: s, loading: prev.loading },
      );
      setSentryUser(nextId);
      if (nextId) {
        identify(nextId);
      } else {
        resetAnalytics();
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      initialSessionResolved.current = true;
      lastUserIdRef.current = data.session?.user?.id ?? null;
      setAuthState({ session: data.session, loading: false });
      setSentryUser(data.session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    user: session?.user ?? null,
    session,
    loading,
    signIn: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      return { error: error?.message ?? null };
    },
    signUp: async (email, password) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login?confirmed=1`,
        },
      });
      if (error) {
        const msg = error.message.toLowerCase();
        if (
          msg.includes("already registered") ||
          msg.includes("already exists") ||
          msg.includes("user already")
        ) {
          return {
            error:
              "An account with this email already exists. Please sign in with your original method.",
          };
        }
        return { error: error.message };
      }
      track("user_signed_up");
      return { error: null };
    },
    signOut: async () => {
      try {
        if (typeof window !== "undefined") {
          const keysToRemove: string[] = [];
          for (let i = 0; i < window.localStorage.length; i++) {
            const k = window.localStorage.key(i);
            if (k && (k.startsWith("caileve_milestone_") || k.startsWith("privee_milestone_"))) keysToRemove.push(k);
          }
          keysToRemove.forEach((k) => window.localStorage.removeItem(k));
        }
      } catch {
        // ignore storage errors
      }
      await supabase.auth.signOut();
    },
  }), [session, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
